import { css, StyleSheet } from 'aphrodite';
import { type FC, type MouseEvent, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { copyToClipboard } from '../../../models/utils/uiUtils';
import { Button } from '../../../web/components-v2/Button';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
	DropdownSeparator,
} from '../../../web/components-v2/Dropdown';
import { CountBadge } from '../../../web/components-v2/CountBadge';
import { Input } from '../../../web/components-v2/Input';
import { FollowUpImportance } from '../../viewModels/queue';
import type { DialAttempts, FocusedLead, FocusedLeadContact, FocusedLeadContactPhone } from '../data';
import type { TextIconState } from '../texting/useRepTextingNumber';
import { LeadAttributePill } from './LeadAttributePill';
import { FOLLOW_UP_DOT, formatFollowUp } from './leadMeta';
import {
	IconChevronRight,
	IconClock,
	IconCopy,
	IconGlobe,
	IconGoogle,
	IconHubSpot,
	IconLinkedIn,
	IconMail,
	IconMapPin,
	IconMessageSquare,
	IconPhone,
	IconPlus,
	IconSearch,
	IconTwitter,
} from './icons';
import { SparButton } from './SparButton';

/**
 * Left column of the Focused Lead View.
 *
 * Pure prop-driven lead info panel: company, website, location/timezone,
 * social icons, last contact, CRM tags, and expandable contact rows.
 * Consumes `FocusedLead` from `../data`; gracefully omits optional sections
 * when their backing fields are not populated.
 *
 * Visual spec mirrors `/tmp/design-bundle/focused-view/project/LeftColumn.jsx`.
 * Styling uses Aphrodite with theme tokens (`var(--ink)`, `var(--border)`,
 * etc.) so light/dark mode is handled automatically by `theme/tokens.css`.
 */
type LeftColumnProps = {
	lead: FocusedLead;
	/** Numbers dialed since this lead was served — drives the highlighted row background. */
	sessionDialedNumbers?: ReadonlySet<string>;
	/** Per-number call history for this lead, keyed by E.164. */
	dialAttempts?: ReadonlyMap<string, DialAttempts>;
	/** Open the Add Contact dialog. */
	onAddContact?: () => void;
	onEditContact?: (contact: FocusedLeadContact) => void;
	onToggleDecisionMaker?: (contact: FocusedLeadContact) => void;
	onDeleteContact?: (contact: FocusedLeadContact) => void;
	/** Place an outbound call to a specific phone number on a contact. */
	onCallContact?: (contact: FocusedLeadContact, phone: FocusedLeadContactPhone) => void;
	/** Open the email composer seeded with this contact. */
	onComposeEmail?: (contact: FocusedLeadContact) => void;
	/** Open the texting dialog for a contact's primary phone. */
	onTextContact?: (contact: FocusedLeadContact, phone: FocusedLeadContactPhone) => void;
	/**
	 * Controls the per-contact text icon: `ready` is clickable, `noNumber` renders
	 * disabled with an explanation, `hidden` (default) omits it.
	 */
	textState?: TextIconState;
	/** Unread inbound texts on the rep's number, keyed by the contact phone's E.164. */
	unreadTextCountByE164?: ReadonlyMap<string, number>;
	/** Trigger the cell-phone enrichment lookup for a contact. */
	onFindCellPhone?: (contact: FocusedLeadContact) => void;
	/** True while a cell-phone lookup is in flight (any contact). */
	findCellPhonePendingId?: string | null;
	/**
	 * True while a dial is waiting on the coaching conversation gate. Disables
	 * every Call affordance (contact rows + company phones) so the rep can't
	 * fire a second dial mid-wait — mirrors the legacy `CallButton`'s
	 * `isStartingCoaching` disabled state.
	 */
	callPending?: boolean;
	/** Open the Edit Company Details dialog. */
	onEditCompany?: () => void;
	/** Place an outbound call to a lead-level (company) phone number. */
	onCallCompanyPhone?: (phone: FocusedLeadContactPhone) => void;
	/** Open the company phone-numbers manager dialog. */
	onManageCompanyPhones?: () => void;
	/**
	 * Start an ad-hoc AI voice-roleplay ("Spar") against a specific contact's
	 * persona — without entering Practice Mode. Only invoked when `enableSpar`.
	 */
	onCallSpar?: (contact: FocusedLeadContact) => void;
	/**
	 * Gates the per-contact Spar button. Coffee-only feature, so the host view
	 * passes `isCoffeeAccount`; the button is hidden entirely when false.
	 */
	enableSpar?: boolean;
	/**
	 * Disables (but still renders) the per-contact Spar button — e.g. while a
	 * real call is active or a Spar session is connecting. Visibility stays
	 * gated by `enableSpar`; this only controls the disabled state.
	 */
	sparDisabled?: boolean;
};

/**
 * Pick the lead's HubSpot deep-link out of `relatedLinks`. Matches the legacy
 * `CallingCard` pattern of relying on the backend-supplied label string.
 * Returns undefined when the account doesn't have a HubSpot integration.
 */
function findHubSpotLink(lead: FocusedLead): string | undefined {
	return lead.relatedLinks?.find(link => /hubspot/i.test(link.label))?.url;
}

function buildLeadShareUrl(companyId: string): string {
	const { origin, pathname } = window.location;
	return `${origin}${pathname}#/focused-view?from=SharedLink&companyId=${encodeURIComponent(companyId)}`;
}

export const LeftColumn: FC<LeftColumnProps> = ({
	lead,
	sessionDialedNumbers,
	dialAttempts,
	onAddContact,
	onEditContact,
	onToggleDecisionMaker,
	onDeleteContact,
	onCallContact,
	onComposeEmail,
	onTextContact,
	textState = 'hidden',
	unreadTextCountByE164,
	onFindCellPhone,
	findCellPhonePendingId,
	callPending,
	onEditCompany,
	onCallCompanyPhone,
	onManageCompanyPhones,
	onCallSpar,
	enableSpar,
	sparDisabled,
}) => {
	const hubspotUrl = findHubSpotLink(lead);
	const handleOpenHubSpot = () => {
		if (!hubspotUrl) return;
		window.open(hubspotUrl, '_blank', 'noopener,noreferrer');
	};
	const handleOpenGoogle = () => {
		const query = [lead.company, lead.location].filter(Boolean).join(' ').trim();
		if (!query) return;
		window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, '_blank', 'noopener,noreferrer');
	};
	const handleCopyLeadLink = () => {
		if (!lead.companyId) return;
		const url = buildLeadShareUrl(lead.companyId);
		void copyToClipboard(url).then(success => {
			if (success) toast.success('Lead link copied to clipboard');
			else toast.error('Failed to copy lead link');
		});
	};
	const hasLocationRow = Boolean(lead.location || lead.timezone);
	const [contactsFilter, setContactsFilter] = useState('');

	const filteredContacts = useMemo(() => {
		const q = contactsFilter.trim().toLowerCase();
		if (!q) return lead.contacts;
		return lead.contacts.filter(c => {
			const phoneHaystack = c.phones.map(p => `${p.label ?? ''} ${p.standard} ${p.display}`).join(' ');
			const haystack = [c.name, c.title, c.email, phoneHaystack].filter(Boolean).join(' ').toLowerCase();
			return haystack.includes(q);
		});
	}, [contactsFilter, lead.contacts]);

	return (
		<div className={css(styles.col, styles.colLeft)}>
			<div className={css(styles.colHead)}>
				<div className={css(styles.colTitle)}>Lead</div>
				<div className={css(styles.headActions)}>
					{lead.company && (
						<Button
							variant='ghost'
							size='icon'
							title='Search on Google'
							aria-label='Search this company on Google'
							onClick={handleOpenGoogle}
						>
							<IconGoogle />
						</Button>
					)}
					{hubspotUrl && (
						<Button
							variant='ghost'
							size='icon'
							title='Open in HubSpot'
							aria-label='Open in HubSpot'
							onClick={handleOpenHubSpot}
						>
							<IconHubSpot />
						</Button>
					)}
					<Dropdown>
						<DropdownEllipsisTrigger />
						<DropdownContent align='end'>
							<DropdownItem onClick={() => onEditCompany?.()} disabled={!onEditCompany}>
								Edit company details
							</DropdownItem>
							<DropdownItem onClick={handleCopyLeadLink} disabled={!lead.companyId}>
								Copy link to lead
							</DropdownItem>
						</DropdownContent>
					</Dropdown>
				</div>
			</div>

			<div className={css(styles.colBody)}>
				{(lead.followUp || lead.statusFlags.length > 0 || lead.crmTags.length > 0) && (
					<div className={css(styles.metaStrip)}>
						{lead.followUp && (
							<span className={css(styles.pill)} title={lead.followUp.name || undefined}>
								<span
									className={css(styles.dot)}
									style={{ background: FOLLOW_UP_DOT[lead.followUp.importance ?? FollowUpImportance.Low] }}
								/>
								{formatFollowUp(lead.followUp)}
							</span>
						)}
						{lead.statusFlags
							// Drop a generic "Follow Up" flag when we already render the
							// richer scheduled-follow-up badge above, to avoid duplication.
							.filter(flag => !(lead.followUp && /^follow.?up$/i.test(flag.label.trim())))
							.map(flag => (
								// Solid badge tinted by the server-supplied `hexColor`, white
								// text — exact parity with the legacy `LeadStatuses` pill.
								<span
									key={flag.label}
									className={css(styles.pill, styles.statusBadge)}
									style={{ background: flag.hexColor || 'var(--ink-faint)' }}
									title={flag.description || undefined}
								>
									{flag.label}
								</span>
							))}
						{lead.crmTags.map(tag => (
							<span key={`${tag.kind ?? ''}:${tag.label}`} className={css(styles.pill, styles.tagPill)}>
								{tag.kind && <span className={css(styles.tagKind)}>{tag.kind}</span>}
								{tag.label}
							</span>
						))}
					</div>
				)}

				<div className={css(styles.companyWrap)}>
					<h1 className={css(styles.companyName)}>{lead.company}</h1>
					{lead.companyId && (
						<button
							type='button'
							className={css(styles.companyCopyBtn)}
							title='Copy link to lead'
							onClick={handleCopyLeadLink}
						>
							<IconCopy size={14} />
						</button>
					)}
				</div>

				{lead.companyId && lead.attributes.length > 0 && (
					<div className={css(styles.attributesRow)}>
						{lead.attributes.map(attribute => (
							<LeadAttributePill key={attribute.id} companyId={lead.companyId} attribute={attribute} />
						))}
					</div>
				)}

				{lead.website && (
					<a
						href={lead.websiteUrl ?? lead.website}
						target='_blank'
						rel='noreferrer noopener'
						aria-label='Open company website in a new tab'
						className={css(styles.websiteLink)}
					>
						<IconGlobe size={13} />
						<span>{lead.website}</span>
					</a>
				)}

				{hasLocationRow && (
					<div className={css(styles.locationRow)}>
						{lead.location && (
							<>
								<IconMapPin size={13} />
								<span>{lead.location}</span>
							</>
						)}
						{lead.location && lead.timezone && <span className={css(styles.locationSep)}>·</span>}
						{lead.timezone && (
							<>
								<IconClock size={13} />
								<span>{lead.timezone}</span>
							</>
						)}
					</div>
				)}

				{(lead.socialProfiles.linkedIn || lead.socialProfiles.twitter) && (
					<div className={css(styles.socialRow)}>
						{lead.socialProfiles.linkedIn && (
							<a
								href={lead.socialProfiles.linkedIn}
								target='_blank'
								rel='noreferrer noopener'
								title='LinkedIn'
								aria-label='Open LinkedIn profile in a new tab'
								className={css(styles.socialLink)}
							>
								<IconLinkedIn size={14} />
							</a>
						)}
						{lead.socialProfiles.twitter && (
							<a
								href={lead.socialProfiles.twitter}
								target='_blank'
								rel='noreferrer noopener'
								title='X / Twitter'
								aria-label='Open X/Twitter profile in a new tab'
								className={css(styles.socialLink)}
							>
								<IconTwitter size={13} />
							</a>
						)}
					</div>
				)}

				<hr className={css(styles.hr)} />
				<div className={css(styles.companyPhonesHeader)}>
					<div className={css(styles.colTitle)}>Phone numbers</div>
					{onManageCompanyPhones && (
						<Button variant='ghost' size='sm' onClick={onManageCompanyPhones}>
							Manage
						</Button>
					)}
				</div>
				{(lead.companyPhones?.length ?? 0) > 0 ? (
					<div className={css(styles.companyPhonesList)}>
						{(lead.companyPhones ?? []).map((phone, i) => (
							<div
								key={`${phone.standard}-${i}`}
								className={css(
									styles.companyPhoneRow,
									sessionDialedNumbers?.has(phone.e164) && styles.companyPhoneRowSessionDialed
								)}
								title={sessionDialedNumbers?.has(phone.e164) ? 'You dialed this number on this lead' : undefined}
							>
								<div className={css(styles.companyPhoneInfo)}>
									{phone.label && <span className={css(styles.detailLabel)}>{phone.label}: </span>}
									<span className={css(styles.detailValue)}>{phone.display}</span>
									{phone.extension && !phone.isInternational && (
										<span className={css(styles.detailValue)}> ext {phone.extension}</span>
									)}
									<DialAttemptBadge attempts={dialAttempts?.get(phone.e164)} />
								</div>
								<button
									type='button'
									className={css(styles.copyBtn)}
									title='Copy phone number'
									onClick={() => copyWithToast('Phone number', phone.standard)}
								>
									<IconCopy size={12} />
								</button>
								<Button
									variant='primary'
									size='sm'
									styles={[styles.companyPhoneCallBtn]}
									title={`Call ${phone.display}`}
									onClick={() => onCallCompanyPhone?.(phone)}
									disabled={!onCallCompanyPhone || callPending}
								>
									Call
								</Button>
							</div>
						))}
					</div>
				) : (
					<div className={css(styles.companyPhonesEmpty)}>No numbers found.</div>
				)}

				{lead.lastContact && (
					<>
						<hr className={css(styles.hr)} />
						<div className={css(styles.lastContactRow)}>
							<div className={css(styles.lastContactIcon)}>
								<IconClock size={14} />
							</div>
							<div>
								<div className={css(styles.colTitle, styles.lastContactLabel)}>Last Contact</div>
								<div className={css(styles.lastContactValue)}>{lead.lastContact}</div>
							</div>
						</div>
					</>
				)}

				<hr className={css(styles.hr)} />

				<div className={css(styles.contactsHeader)}>
					<div className={css(styles.contactsSearchWrap)}>
						<IconSearch size={13} sw={1.8} color='var(--ink-faint)' />
						<Input
							type='search'
							value={contactsFilter}
							onChange={e => setContactsFilter(e.target.value)}
							placeholder='Filter contacts'
							styles={[styles.contactsSearchInput]}
						/>
					</div>
					<Button variant='ghost' size='sm' title='Add contact' onClick={onAddContact} disabled={!onAddContact}>
						<IconPlus size={12} />
						Add
					</Button>
				</div>
				<div className={css(styles.contactsList)}>
					{filteredContacts.length === 0 && contactsFilter ? (
						<div className={css(styles.contactsEmpty)}>No contacts match &ldquo;{contactsFilter}&rdquo;</div>
					) : (
						filteredContacts.map(contact => (
							<ContactRow
								key={contact.id}
								contact={contact}
								hasPriorContact={Boolean(lead.lastContact)}
								sessionDialedNumbers={sessionDialedNumbers}
								dialAttempts={dialAttempts}
								onEdit={onEditContact}
								onToggleDecisionMaker={onToggleDecisionMaker}
								onDelete={onDeleteContact}
								onCall={onCallContact}
								onComposeEmail={onComposeEmail}
								onTextContact={onTextContact}
								textState={textState}
								unreadTextCountByE164={unreadTextCountByE164}
								onFindCellPhone={onFindCellPhone}
								findCellPhonePending={findCellPhonePendingId === contact.id}
								callDisabled={callPending}
								onCallSpar={onCallSpar}
								enableSpar={enableSpar}
								sparDisabled={sparDisabled}
							/>
						))
					)}
				</div>
			</div>
		</div>
	);
};

type ContactRowProps = {
	contact: FocusedLeadContact;
	/**
	 * True when the lead has prior logged contact (`lead.lastContact` is set).
	 * Relabels the call affordances to "Call Again" so reps can tell at a glance
	 * the lead was already dialed.
	 */
	hasPriorContact?: boolean;
	sessionDialedNumbers?: ReadonlySet<string>;
	dialAttempts?: ReadonlyMap<string, DialAttempts>;
	onEdit?: (contact: FocusedLeadContact) => void;
	onToggleDecisionMaker?: (contact: FocusedLeadContact) => void;
	onDelete?: (contact: FocusedLeadContact) => void;
	onCall?: (contact: FocusedLeadContact, phone: FocusedLeadContactPhone) => void;
	/** Open the email composer seeded with this contact. */
	onComposeEmail?: (contact: FocusedLeadContact) => void;
	onTextContact?: (contact: FocusedLeadContact, phone: FocusedLeadContactPhone) => void;
	textState?: TextIconState;
	unreadTextCountByE164?: ReadonlyMap<string, number>;
	onFindCellPhone?: (contact: FocusedLeadContact) => void;
	findCellPhonePending?: boolean;
	/**
	 * Disables this row's call affordances (the header phone icon + the expanded
	 * Call button) while a dial is waiting on the coaching gate. Legacy parity:
	 * `CallButton`'s `isStartingCoaching` disabled state.
	 */
	callDisabled?: boolean;
	/** Start an ad-hoc AI roleplay against this contact's persona. */
	onCallSpar?: (contact: FocusedLeadContact) => void;
	/** Render the per-contact Spar button (coffee-gated by the host view). */
	enableSpar?: boolean;
	/** Disable the per-contact Spar button (e.g. a real call is active/connecting). */
	sparDisabled?: boolean;
};

const copyWithToast = (label: string, value: string) => {
	void copyToClipboard(value).then(success => {
		if (success) toast.success(`${label} copied to clipboard`);
		else toast.error(`${label} failed to copy to clipboard`);
	});
};

/**
 * Avatar for a contact row. Renders the profile image when one is available
 * and falls back to initials when not (or when the image fails to load).
 * Mirrors the legacy `<Avatar entity={c} />` behavior, but without pulling in
 * the MobX-based component.
 */
const ContactAvatar: FC<{ contact: FocusedLeadContact }> = ({ contact }) => {
	const [failed, setFailed] = useState(false);
	const showImage = !!contact.imageUrl && !failed;
	return (
		<div className={css(styles.avatar)}>
			{showImage ? (
				<img src={contact.imageUrl} alt='' className={css(styles.avatarImage)} onError={() => setFailed(true)} />
			) : (
				contact.initials
			)}
		</div>
	);
};

function dialHint(dialedThisSession: boolean, attempts: DialAttempts | undefined): string {
	if (dialedThisSession) return ' · dialed on this lead';
	const count = attempts?.count ?? 0;
	if (count <= 0) return '';
	return count === 1 ? ' · tried once before' : ` · tried ${count} times before`;
}

const DialAttemptBadge: FC<{ attempts: DialAttempts | undefined }> = ({ attempts }) => {
	if (!attempts || attempts.count <= 0) return null;
	const times = attempts.count === 1 ? '1 time' : `${attempts.count} times`;
	const last = attempts.lastAttemptAt
		? ` · last ${new Date(attempts.lastAttemptAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
		: '';
	return (
		<span className={css(styles.attemptBadge)} title={`Dialed ${times}${last}`}>
			{attempts.count}
			{'×'}
		</span>
	);
};

const ContactRow: FC<ContactRowProps> = ({
	contact,
	hasPriorContact,
	sessionDialedNumbers,
	dialAttempts,
	onEdit,
	onToggleDecisionMaker,
	onDelete,
	onCall,
	onComposeEmail,
	onTextContact,
	textState = 'hidden',
	unreadTextCountByE164,
	onFindCellPhone,
	findCellPhonePending,
	callDisabled,
	onCallSpar,
	enableSpar,
	sparDisabled,
}) => {
	const [expanded, setExpanded] = useState<boolean>(Boolean(contact.primary));
	const isDM = Boolean(contact.primary);
	const primaryPhone = contact.phones[0];
	// Once the lead has prior logged contact, relabel the call affordances so a
	// rep can tell at a glance the lead was already dialed.
	const callVerb = hasPriorContact ? 'Call Again' : 'Call';
	const primaryPhoneHint = primaryPhone
		? dialHint(sessionDialedNumbers?.has(primaryPhone.e164) ?? false, dialAttempts?.get(primaryPhone.e164))
		: '';
	const hasMobile = contact.phones.some(p => (p.label ?? '').toLowerCase().includes('mobile'));
	const showFindCellPhone = !hasMobile;
	// Gate on both the handler's presence and the coaching-gate wait, so the
	// header phone icon goes inert (no button role / click handler) while a dial
	// is pending — mirrors the expanded Call button's disabled state below.
	const canCall = !!onCall && !callDisabled;
	const canText = textState === 'ready' && !!onTextContact;
	const showTextIcon = textState !== 'hidden' && !!primaryPhone;
	const unreadTexts = contact.phones.reduce((sum, p) => sum + (unreadTextCountByE164?.get(p.e164) ?? 0), 0);

	const stopMenuClick = (e: MouseEvent<HTMLDivElement>) => {
		e.stopPropagation();
	};

	return (
		<div className={css(styles.contactRow)}>
			<div className={css(styles.contactRowHead)}>
				<button
					type='button'
					onClick={() => setExpanded(value => !value)}
					className={css(styles.contactHeaderBtn)}
					aria-expanded={expanded}
				>
					<div className={css(styles.contactHead)}>
						<ContactAvatar contact={contact} />
						<div className={css(styles.contactTextCol)}>
							<div className={css(styles.contactNameLine)}>
								<span className={css(styles.contactName)}>{contact.name}</span>
								<span className={css(styles.contactReachIcons)}>
									{primaryPhone && (
										<span
											role={canCall ? 'button' : undefined}
											tabIndex={canCall ? 0 : undefined}
											className={css(styles.contactReachIcon, canCall && styles.contactReachIconClickable)}
											aria-hidden={canCall ? undefined : true}
											aria-label={canCall ? `${callVerb} ${contact.name}` : undefined}
											title={
												canCall
													? `${callVerb} ${primaryPhone.display}${primaryPhoneHint}`
													: `${contact.phones.length} phone number${contact.phones.length === 1 ? '' : 's'}`
											}
											onClick={
												canCall
													? e => {
															// Don't let the click bubble to the header toggle button.
															e.stopPropagation();
															onCall?.(contact, primaryPhone);
														}
													: undefined
											}
											onKeyDown={
												canCall
													? e => {
															if (e.key === 'Enter' || e.key === ' ') {
																e.preventDefault();
																e.stopPropagation();
																onCall?.(contact, primaryPhone);
															}
														}
													: undefined
											}
										>
											<IconPhone size={15} />
										</span>
									)}
									{contact.email && (
										<span
											role={onComposeEmail ? 'button' : undefined}
											tabIndex={onComposeEmail ? 0 : undefined}
											className={css(styles.contactReachIcon, onComposeEmail && styles.contactReachIconClickable)}
											aria-hidden={onComposeEmail ? undefined : true}
											aria-label={onComposeEmail ? `Email ${contact.name}` : undefined}
											title={onComposeEmail ? `Email ${contact.email}` : 'Has email'}
											onClick={
												onComposeEmail
													? e => {
															e.stopPropagation();
															onComposeEmail(contact);
														}
													: undefined
											}
											onKeyDown={
												onComposeEmail
													? e => {
															if (e.key === 'Enter' || e.key === ' ') {
																e.preventDefault();
																e.stopPropagation();
																onComposeEmail(contact);
															}
														}
													: undefined
											}
										>
											<IconMail size={15} />
										</span>
									)}
									{showTextIcon && (
										<span
											role={canText ? 'button' : undefined}
											tabIndex={canText ? 0 : undefined}
											className={css(
												styles.contactReachIcon,
												canText ? styles.contactReachIconClickable : styles.contactReachIconDisabled
											)}
											aria-label={`Text ${contact.name}`}
											aria-disabled={canText ? undefined : true}
											data-testid='contact-text-icon'
											title={
												unreadTexts > 0
													? `${unreadTexts} unread text${unreadTexts === 1 ? '' : 's'}`
													: canText
														? `Text ${primaryPhone.display}`
														: 'No texting number is provisioned for you yet.'
											}
											onClick={
												canText
													? e => {
															e.stopPropagation();
															onTextContact?.(contact, primaryPhone);
														}
													: undefined
											}
											onKeyDown={
												canText
													? e => {
															if (e.key === 'Enter' || e.key === ' ') {
																e.preventDefault();
																e.stopPropagation();
																onTextContact?.(contact, primaryPhone);
															}
														}
													: undefined
											}
										>
											<IconMessageSquare size={15} />
											<CountBadge
												count={unreadTexts}
												data-testid='contact-text-unread'
												styles={[styles.unreadTextBadge]}
											/>
										</span>
									)}
								</span>
								{isDM && <span className={css(styles.dmBadge)}>DM</span>}
							</div>
							{contact.title && <div className={css(styles.contactTitle)}>{contact.title}</div>}
						</div>
					</div>
				</button>
				<div className={css(styles.contactRightActions)} onClick={stopMenuClick}>
					<Dropdown>
						<DropdownEllipsisTrigger />
						<DropdownContent align='end'>
							<DropdownItem onClick={() => onEdit?.(contact)} disabled={!onEdit}>
								Edit
							</DropdownItem>
							<DropdownItem onClick={() => onToggleDecisionMaker?.(contact)} disabled={!onToggleDecisionMaker}>
								{isDM ? 'Unmark as decision maker' : 'Mark as decision maker'}
							</DropdownItem>
							<DropdownSeparator />
							<DropdownItem onClick={() => onDelete?.(contact)} disabled={!onDelete}>
								Delete
							</DropdownItem>
						</DropdownContent>
					</Dropdown>
					<div
						className={css(styles.chevron)}
						style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0)' }}
						aria-hidden
					>
						<IconChevronRight size={14} />
					</div>
				</div>
			</div>
			{expanded && (
				<div className={css(styles.contactDetails)} onClick={stopMenuClick}>
					<div className={css(styles.contactBodyRow)}>
						<div className={css(styles.contactDetailsCol)}>
							{contact.phones.map((phone, i) => {
								const dialedThisSession = sessionDialedNumbers?.has(phone.e164) ?? false;
								return (
									<div
										key={`${phone.standard}-${i}`}
										className={css(styles.detailLine, dialedThisSession && styles.phoneLineSessionDialed)}
										title={dialedThisSession ? 'You dialed this number on this lead' : undefined}
									>
										{phone.label && <span className={css(styles.detailLabel)}>{phone.label}: </span>}
										<span className={css(styles.detailValue)}>{phone.display}</span>
										{phone.extension && !phone.isInternational && (
											<span className={css(styles.detailValue)}> ext {phone.extension}</span>
										)}
										<DialAttemptBadge attempts={dialAttempts?.get(phone.e164)} />
										<button
											type='button'
											className={css(styles.copyBtn)}
											title='Copy phone number'
											onClick={() => copyWithToast('Phone number', phone.standard)}
										>
											<IconCopy size={12} />
										</button>
										<button
											type='button'
											className={css(styles.phoneCallBtn)}
											title={`${callVerb} ${phone.display}${dialHint(dialedThisSession, dialAttempts?.get(phone.e164))}`}
											aria-label={`${callVerb} ${phone.display}`}
											disabled={!onCall || callDisabled}
											onClick={() => onCall?.(contact, phone)}
										>
											<IconPhone size={12} />
										</button>
									</div>
								);
							})}
							{showFindCellPhone && (
								<button
									type='button'
									className={css(styles.findCellLink)}
									onClick={() => onFindCellPhone?.(contact)}
									disabled={!onFindCellPhone || findCellPhonePending}
								>
									{findCellPhonePending ? 'Looking up cell phone…' : 'Find cell phone'}
								</button>
							)}
							{contact.emailAddresses.map(email => (
								<div key={email.value} className={css(styles.detailLine)}>
									<a href={`mailto:${email.value}`} className={css(styles.emailLink)}>
										{email.value}
									</a>
									{email.isUnsubscribed && (
										<span className={css(styles.unsubscribedBadge)} title='This contact is unsubscribed from email'>
											unsubscribed
										</span>
									)}
									<button
										type='button'
										className={css(styles.copyBtn)}
										title='Copy email'
										onClick={() => copyWithToast('Email', email.value)}
									>
										<IconCopy size={12} />
									</button>
								</div>
							))}
						</div>
						<div className={css(styles.contactCallActions)}>
							{enableSpar && (
								<SparButton onClick={() => onCallSpar?.(contact)} disabled={!onCallSpar || sparDisabled} />
							)}
							{primaryPhone && (
								<Button
									variant='primary'
									size='sm'
									styles={[styles.callBtn]}
									onClick={() => onCall?.(contact, primaryPhone)}
									disabled={!onCall || callDisabled}
									title={`${callVerb} ${primaryPhone.display}`}
								>
									{callVerb}
								</Button>
							)}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

const styles = StyleSheet.create({
	col: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		minHeight: 0,
		height: '100vh',
	},
	colLeft: {
		borderRight: '1px solid var(--border)',
		background: 'var(--bg)',
	},
	colHead: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		height: 52,
		padding: '0 20px',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
	},
	colTitle: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'var(--ink-faint)',
	},
	headActions: {
		display: 'flex',
		gap: 2,
	},
	colBody: {
		flex: 1,
		overflowY: 'auto',
		minHeight: 0,
		padding: '24px 20px 32px',
	},
	iconBtn: {
		width: 30,
		height: 30,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 8,
		color: 'var(--ink-muted)',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		padding: 0,
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	stageBadges: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 6,
		marginBottom: 20,
	},
	pill: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		height: 24,
		padding: '0 10px',
		borderRadius: 999,
		fontSize: 12,
		fontWeight: 500,
		background: 'var(--bg-sunken)',
		color: 'var(--ink)',
		border: '1px solid var(--border)',
	},
	pillSolid: {
		background: 'var(--ink)',
		color: 'var(--bg)',
		borderColor: 'transparent',
	},
	pillAccent: {
		background: 'var(--accent-soft)',
		color: 'var(--accent-ink)',
		borderColor: 'transparent',
	},
	dot: {
		width: 6,
		height: 6,
		borderRadius: 999,
		display: 'inline-block',
	},
	companyWrap: {
		marginBottom: 4,
		display: 'flex',
		alignItems: 'center',
		gap: 8,
	},
	companyCopyBtn: {
		width: 28,
		height: 28,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 6,
		color: 'var(--ink-muted)',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		padding: 0,
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	companyName: {
		fontSize: 32,
		fontWeight: 600,
		margin: 0,
		letterSpacing: '-0.02em',
		lineHeight: 1.1,
		color: 'var(--ink)',
	},
	websiteLink: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		color: 'var(--ink-muted)',
		fontSize: 13,
		marginTop: 8,
		textDecoration: 'none',
	},
	locationRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		color: 'var(--ink-muted)',
		fontSize: 13,
		marginTop: 6,
	},
	locationSep: {
		color: 'var(--ink-faint)',
	},
	socialRow: {
		display: 'flex',
		gap: 4,
		marginTop: 16,
	},
	socialLink: {
		width: 30,
		height: 30,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 8,
		color: 'var(--ink-muted)',
		background: 'transparent',
		textDecoration: 'none',
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	companyPhonesHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 8,
		marginBottom: 8,
	},
	companyPhonesList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	companyPhoneRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		padding: '6px 8px',
		borderRadius: 8,
		background: 'var(--bg-elev)',
		border: '1px solid var(--border)',
	},
	companyPhoneInfo: {
		flex: 1,
		minWidth: 0,
		fontSize: 12.5,
		color: 'var(--ink)',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	companyPhoneCallBtn: {
		// Layout-only override; fill, hover, active, and disabled treatment come
		// from the components-v2 Button `primary` variant.
		flexShrink: 0,
	},
	companyPhonesEmpty: {
		padding: '12px 14px',
		fontSize: 12.5,
		color: 'var(--ink-muted)',
		textAlign: 'center',
		background: 'var(--accent-soft)',
		borderRadius: 8,
	},
	hr: {
		height: 1,
		background: 'var(--border)',
		border: 'none',
		margin: '24px 0',
	},
	lastContactRow: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 10,
	},
	lastContactIcon: {
		width: 28,
		height: 28,
		borderRadius: 8,
		background: 'var(--bg-sunken)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		color: 'var(--ink-muted)',
		flexShrink: 0,
	},
	lastContactLabel: {
		marginBottom: 2,
	},
	lastContactValue: {
		fontSize: 13,
		color: 'var(--ink)',
	},
	sectionLabel: {
		marginBottom: 10,
	},
	tagsWrap: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 6,
	},
	metaStrip: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: 6,
		marginBottom: 10,
	},
	// Solid status badge — background is set inline from the flag's `hexColor`,
	// white text, no border (legacy `LeadStatuses` parity).
	statusBadge: {
		color: '#fff',
		border: 'none',
	},
	attributesRow: {
		display: 'flex',
		flexWrap: 'wrap',
		alignItems: 'center',
		gap: 6,
		marginTop: 8,
		marginBottom: 2,
	},
	tagPill: {
		fontSize: 11.5,
		height: 22,
		padding: '0 9px',
	},
	tagKind: {
		color: 'var(--ink-faint)',
		fontSize: 10,
		marginRight: 2,
	},
	contactsHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: 8,
		marginBottom: 10,
	},
	contactsSearchWrap: {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		padding: '0 8px',
		background: 'var(--bg-sunken)',
		border: '1px solid var(--border)',
		borderRadius: 8,
	},
	contactsSearchInput: {
		flex: 1,
		minWidth: 0,
		height: 28,
		padding: 0,
		border: 'none',
		background: 'transparent',
		fontSize: 12.5,
		color: 'var(--ink)',
	},
	contactsEmpty: {
		padding: '12px 8px',
		fontSize: 12,
		color: 'var(--ink-muted)',
		textAlign: 'center',
	},
	contactsList: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
	},
	contactRow: {
		display: 'flex',
		flexDirection: 'column',
		borderRadius: 8,
		paddingRight: 6,
		transition: 'background 120ms',
		':hover': {
			background: 'var(--hover)',
		},
	},
	contactRowHead: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: 4,
	},
	contactHeaderBtn: {
		flex: 1,
		minWidth: 0,
		textAlign: 'left',
		padding: '10px 8px',
		borderRadius: 8,
		cursor: 'pointer',
		background: 'transparent',
		border: 'none',
		color: 'inherit',
		font: 'inherit',
	},
	contactRightActions: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		paddingTop: 12,
		flexShrink: 0,
	},
	contactHead: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
	},
	avatar: {
		width: 32,
		height: 32,
		borderRadius: 999,
		background: 'var(--bg-sunken)',
		border: '1px solid var(--border)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: 11,
		fontWeight: 600,
		color: 'var(--ink-muted)',
		flexShrink: 0,
		overflow: 'hidden',
	},
	avatarImage: {
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},
	contactTextCol: {
		minWidth: 0,
		flex: 1,
	},
	contactNameLine: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		fontSize: 13,
		fontWeight: 500,
		color: 'var(--ink)',
	},
	contactName: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	contactReachIcons: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 4,
		color: 'var(--ink-faint)',
		flexShrink: 0,
	},
	contactReachIcon: {
		position: 'relative',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 22,
		height: 22,
		borderRadius: 6,
	},
	unreadTextBadge: {
		top: -4,
		right: -4,
	},
	contactReachIconDisabled: {
		color: 'var(--ink-faint)',
		opacity: 0.6,
		cursor: 'not-allowed',
	},
	contactReachIconClickable: {
		color: 'var(--ink-muted)',
		cursor: 'pointer',
		transition: 'background 120ms ease, color 120ms ease',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--accent)',
		},
		':focus-visible': {
			outline: '2px solid var(--ring)',
			outlineOffset: 0,
		},
	},
	dmBadge: {
		fontSize: 10,
		fontWeight: 600,
		letterSpacing: '0.04em',
		color: 'var(--accent-ink)',
		background: 'var(--accent-soft)',
		padding: '1px 6px',
		borderRadius: 4,
	},
	contactTitle: {
		fontSize: 12,
		color: 'var(--ink-muted)',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	chevron: {
		color: 'var(--ink-faint)',
		transition: 'transform 160ms',
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: 18,
		height: 18,
	},
	contactDetails: {
		marginLeft: 8,
		marginTop: 8,
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	contactBodyRow: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		gap: 12,
	},
	contactDetailsCol: {
		flex: 1,
		minWidth: 0,
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
	},
	detailLine: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		fontSize: 12.5,
		color: 'var(--ink)',
		lineHeight: 1.4,
		flexWrap: 'wrap',
	},
	// A shadow, not padding: this row has no width to spare and padding wraps the number.
	phoneLineSessionDialed: {
		background: 'var(--dialed-bg)',
		borderRadius: 3,
		boxShadow: '0 0 0 3px var(--dialed-bg)',
	},
	companyPhoneRowSessionDialed: {
		background: 'var(--dialed-bg)',
		borderColor: 'var(--dialed-border)',
	},
	attemptBadge: {
		flexShrink: 0,
		fontSize: 10,
		fontWeight: 600,
		lineHeight: 1,
		padding: '2px 5px',
		borderRadius: 4,
		background: 'var(--bg-sunken)',
		color: 'var(--ink-muted)',
		cursor: 'default',
	},
	detailLabel: {
		color: 'var(--ink-muted)',
	},
	detailValue: {
		color: 'var(--ink)',
		whiteSpace: 'nowrap',
	},
	copyBtn: {
		width: 20,
		height: 20,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 4,
		color: 'var(--ink-faint)',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		padding: 0,
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--ink)',
		},
	},
	phoneCallBtn: {
		width: 20,
		height: 20,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: 4,
		color: 'var(--ink-muted)',
		background: 'transparent',
		border: 'none',
		cursor: 'pointer',
		padding: 0,
		transition: 'background 120ms, color 120ms',
		':hover': {
			background: 'var(--hover)',
			color: 'var(--accent)',
		},
		':disabled': {
			color: 'var(--ink-faint)',
			cursor: 'default',
		},
	},
	emailLink: {
		color: 'var(--ink)',
		textDecoration: 'underline',
		fontSize: 12.5,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		minWidth: 0,
	},
	unsubscribedBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		fontSize: 10,
		fontWeight: 600,
		letterSpacing: '0.04em',
		textTransform: 'uppercase',
		padding: '1px 6px',
		borderRadius: 4,
		background: 'rgba(239, 68, 68, 0.12)',
		color: '#EF4444',
	},
	findCellLink: {
		alignSelf: 'flex-start',
		background: 'transparent',
		border: 'none',
		color: 'var(--accent)',
		fontSize: 12.5,
		padding: 0,
		cursor: 'pointer',
		textDecoration: 'underline',
		':hover': {
			color: 'var(--accent-ink)',
		},
		':disabled': {
			color: 'var(--ink-faint)',
			cursor: 'default',
			textDecoration: 'none',
		},
	},
	contactCallActions: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		gap: 4,
	},
	callBtn: {
		// Layout-only override; fill, hover, active, and disabled treatment come
		// from the components-v2 Button `primary` variant.
		flexShrink: 0,
	},
});
